"use client";

import { useState, useCallback, useRef, useEffect, useId } from "react";
import { useSession } from "next-auth/react";
import { PageLayout } from "@/components/ui/PageLayout";
import { BankSolid, SeedlingsSolid, SaveSolid } from "@2hoch1/pixel-icon-library-react";
import type { ComponentType, SVGProps } from "react";
import Link from "next/link";
import { Loader2, Fingerprint, X, CheckCircle2, ExternalLink } from "lucide-react";
import { usePasskey } from "@/contexts/PasskeyContext";
import {
  TESTNET_TOKENS,
  POOL_ADDRESSES,
  ADAPTER_IDS,
  TOKEN_UNIT,
  getTxExplorerUrl,
} from "@/lib/hoops-contracts";
import type { LpPlan, RedeemPlan } from "@/lib/hoops-passkey-kit";
import { TxLogPanel, createLogEntry } from "@/components/ui/TxLogPanel";
import type { LogEntry } from "@/components/ui/TxLogPanel";
import { useSmartAccountBalances } from "@/hooks/useSmartAccountBalances";
import { recordActivity } from "@/services/activity.service";

// ---------------------------------------------------------------------------
// Strategy definitions
// ---------------------------------------------------------------------------

interface StrategyPool {
  protocol: string;
  pair: string;
  allocation: number;
  apr: string;
}

interface Strategy {
  id: number;
  slug: "FIXED" | "FLOAT" | "LOCK";
  name: string;
  subtitle: string;
  description: string;
  riskLevel: "Low" | "Medium" | "High";
  apy: string;
  tokens: string[];
  pools: StrategyPool[];
  status: "live" | "coming_soon";
  icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;
  accentColor: string;
  deployColor: string;
  deployTextColor: string;
}

const STRATEGIES: Strategy[] = [
  {
    id: 1,
    slug: "FIXED",
    name: "FIXED",
    subtitle: "Stablecoin Yield",
    icon: BankSolid,
    description:
      "Low-risk strategy targeting stablecoin and blue-chip pools. USDC deposits are allocated across Aqua and Soroswap USDC/XLM pools for steady, predictable yield with minimal impermanent loss.",
    riskLevel: "Low",
    apy: "5.0%",
    tokens: ["USDC", "XLM"],
    pools: [
      { protocol: "Aqua", pair: "USDC/XLM", allocation: 50, apr: "4.8%" },
      { protocol: "Soroswap", pair: "USDC/XLM", allocation: 50, apr: "5.2%" },
    ],
    status: "live",
    accentColor: "#3ecf7e",
    deployColor: "#3ecf7e",
    deployTextColor: "#051a0e",
  },
  {
    id: 2,
    slug: "FLOAT",
    name: "FLOAT",
    subtitle: "Mixed Yield",
    icon: SeedlingsSolid,
    description:
      "Higher-yield strategy spreading USDC across multiple protocols and token pairs. Targets Aqua, Phoenix, and Soroswap pools for diversified exposure with dynamic rebalancing potential.",
    riskLevel: "Medium",
    apy: "11.8%",
    tokens: ["USDC", "XLM"],
    pools: [
      { protocol: "Aqua", pair: "USDC/XLM", allocation: 34, apr: "4.8%" },
      { protocol: "Phoenix", pair: "XLM/USDC", allocation: 33, apr: "8.7%" },
      { protocol: "Soroswap", pair: "USDC/XLM", allocation: 33, apr: "15.1%" },
    ],
    status: "coming_soon",
    accentColor: "#a78bfa",
    deployColor: "#a78bfa",
    deployTextColor: "#130d24",
  },
  {
    id: 3,
    slug: "LOCK",
    name: "LOCK",
    subtitle: "Lending Yield",
    icon: SaveSolid,
    description:
      "Fixed-term lending strategy via Blend protocol. Deposit USDC into lending markets for stable interest income. Requires lockup periods but offers the most predictable returns.",
    riskLevel: "Low",
    apy: "6-8%",
    tokens: ["USDC"],
    pools: [
      { protocol: "Blend", pair: "USDC Lending", allocation: 100, apr: "~6-8%" },
    ],
    status: "coming_soon",
    accentColor: "#fb923c",
    deployColor: "#141414",
    deployTextColor: "#3a3a3a",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getProtocolColor(protocol: string): string {
  switch (protocol.toLowerCase()) {
    case "aqua":     return "#38bdf8";
    case "phoenix":  return "#a78bfa";
    case "soroswap": return "#4ade80";
    case "blend":    return "#fb923c";
    default:         return "#555";
  }
}

interface RiskBadgeStyles {
  color: string;
  borderColor: string;
  background: string;
}

function getRiskBadgeStyles(level: "Low" | "Medium" | "High"): RiskBadgeStyles {
  switch (level) {
    case "Low":
      return { color: "#3ecf7e", borderColor: "#3ecf7e33", background: "#3ecf7e0d" };
    case "Medium":
      return { color: "#e8c96a", borderColor: "#e8c96a33", background: "#e8c96a0d" };
    case "High":
      return { color: "#f87171", borderColor: "#f8717133", background: "#f871710d" };
  }
}

function formatBalance(raw: bigint): string {
  const whole = raw / BigInt(TOKEN_UNIT);
  const frac = (raw % BigInt(TOKEN_UNIT)).toString().padStart(7, "0").slice(0, 2);
  return `${whole.toLocaleString()}.${frac}`;
}

// ---------------------------------------------------------------------------
// Deposit status type
// ---------------------------------------------------------------------------

type DepositStatus = "idle" | "signing" | "submitting" | "success" | "error";

// ---------------------------------------------------------------------------
// Deposit modal
// ---------------------------------------------------------------------------

interface DepositModalProps {
  strategy: Strategy;
  onClose: () => void;
}

function DepositModal({ strategy, onClose }: DepositModalProps) {
  const { deposit, queryBalance } = usePasskey();
  const [amount, setAmount] = useState("");
  const [usdcBalance, setUsdcBalance] = useState<bigint | null>(null);
  const [status, setStatus] = useState<DepositStatus>("idle");
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const busyRef = useRef(false);
  const inputId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);

  const addLog = useCallback(
    (level: LogEntry["level"], message: string, txHash?: string) => {
      setLogs((prev) => [...prev, createLogEntry(level, message, txHash)]);
    },
    [],
  );

  // Fetch USDC balance on mount
  useEffect(() => {
    let cancelled = false;
    queryBalance(TESTNET_TOKENS.USDC).then((bal) => {
      if (!cancelled) setUsdcBalance(bal);
    }).catch(() => {
      if (!cancelled) setUsdcBalance(0n);
    });
    return () => { cancelled = true; };
  }, [queryBalance]);

  // Trap focus — close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && status !== "signing" && status !== "submitting") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => { document.removeEventListener("keydown", handleKeyDown); };
  }, [onClose, status]);

  // Prevent background scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  function parseAmount(value: string): bigint | null {
    const parsed = parseFloat(value);
    if (!isFinite(parsed) || parsed <= 0) return null;
    return BigInt(Math.round(parsed * TOKEN_UNIT));
  }

  const handleDeposit = useCallback(async () => {
    if (busyRef.current) return;
    const totalUsdc = parseAmount(amount);
    if (totalUsdc === null) return;

    busyRef.current = true;
    setStatus("signing");

    const XLM_RESERVE = 10n * BigInt(TOKEN_UNIT); // Keep 10 XLM for gas fees
    const deadline = Math.floor(Date.now() / 1000) + 600;

    try {
      // Step 1: query balances
      addLog("info", "Querying account balances...");
      const [xlmBalance, usdcBal] = await Promise.all([
        queryBalance(TESTNET_TOKENS.XLM),
        queryBalance(TESTNET_TOKENS.USDC),
      ]);

      const availableXlm = xlmBalance > XLM_RESERVE ? xlmBalance - XLM_RESERVE : 0n;
      const xlmFormatted = (Number(availableXlm) / TOKEN_UNIT).toFixed(2);
      const usdcFormatted = (Number(usdcBal) / TOKEN_UNIT).toFixed(2);

      addLog("info", `Available: ${usdcFormatted} USDC, ${xlmFormatted} XLM (10 XLM reserved for gas)`);

      if (availableXlm <= 0n) {
        addLog("error", "Insufficient XLM for paired liquidity. Fund your account with XLM first.");
        setStatus("error");
        busyRef.current = false;
        return;
      }

      // Step 2: build plans
      let plans: LpPlan[];
      const poolCount = strategy.slug === "FIXED" ? 2n : strategy.slug === "FLOAT" ? 3n : 0n;
      if (poolCount === 0n) {
        busyRef.current = false;
        return;
      }

      const usdcPerPool = totalUsdc / poolCount;
      const xlmPerPool = availableXlm / poolCount;
      const poolLabel = strategy.slug === "FIXED" ? "Aqua + Soroswap" : "Aqua + Phoenix + Soroswap";
      addLog("info", `Building ${String(poolCount)}-pool split: ${poolLabel}`);

      if (strategy.slug === "FIXED") {
        plans = [
          {
            tokenA: TESTNET_TOKENS.USDC,
            tokenB: TESTNET_TOKENS.XLM,
            amountA: usdcPerPool,
            amountB: xlmPerPool,
            adapterId: BigInt(ADAPTER_IDS.AQUA),
            poolAddress: POOL_ADDRESSES.AQUA_POOL,
          },
          {
            tokenA: TESTNET_TOKENS.USDC,
            tokenB: TESTNET_TOKENS.XLM,
            amountA: usdcPerPool,
            amountB: xlmPerPool,
            adapterId: BigInt(ADAPTER_IDS.SOROSWAP),
            poolAddress: POOL_ADDRESSES.SOROSWAP_PAIR,
          },
        ];
      } else {
        plans = [
          {
            tokenA: TESTNET_TOKENS.USDC,
            tokenB: TESTNET_TOKENS.XLM,
            amountA: usdcPerPool,
            amountB: xlmPerPool,
            adapterId: BigInt(ADAPTER_IDS.AQUA),
            poolAddress: POOL_ADDRESSES.AQUA_POOL,
          },
          {
            tokenA: TESTNET_TOKENS.USDC,
            tokenB: TESTNET_TOKENS.XLM,
            amountA: usdcPerPool,
            amountB: xlmPerPool,
            adapterId: BigInt(ADAPTER_IDS.PHOENIX),
            poolAddress: POOL_ADDRESSES.PHOENIX_POOL,
          },
          {
            tokenA: TESTNET_TOKENS.USDC,
            tokenB: TESTNET_TOKENS.XLM,
            amountA: usdcPerPool,
            amountB: xlmPerPool,
            adapterId: BigInt(ADAPTER_IDS.SOROSWAP),
            poolAddress: POOL_ADDRESSES.SOROSWAP_PAIR,
          },
        ];
      }

      // Step 3: passkey authentication
      addLog("info", "Waiting for passkey authentication...");

      // Step 4: submit
      setStatus("submitting");
      addLog("info", "Submitting to Stellar network...");
      const result = await deposit(TESTNET_TOKENS.USDC, totalUsdc, plans, deadline);

      if (result?.success) {
        addLog("success", "Deposit successful!", result.hash);
        setStatus("success");
        void recordActivity({
          txHash: result.hash,
          action: "deposit",
          strategyId: strategy.slug,
          amounts: { usdc: Number(totalUsdc) / TOKEN_UNIT },
          status: "success",
        });
      } else {
        const errMsg = result?.error ?? "Deposit failed. Please try again.";
        addLog("error", errMsg.length > 200 ? errMsg.slice(0, 200) + "..." : errMsg);
        setStatus("error");
        void recordActivity({
          txHash: "",
          action: "deposit",
          strategyId: strategy.slug,
          status: "error",
          errorMessage: errMsg.slice(0, 200),
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Deposit failed. Please try again.";
      addLog("error", msg.length > 200 ? msg.slice(0, 200) + "..." : msg);
      setStatus("error");
      void recordActivity({
        txHash: "",
        action: "deposit",
        strategyId: strategy.slug,
        status: "error",
        errorMessage: msg.slice(0, 200),
      });
    } finally {
      busyRef.current = false;
    }
  }, [amount, deposit, queryBalance, strategy.slug, addLog]);

  const amountInvalid = amount !== "" && parseAmount(amount) === null;
  const isBusy = status === "signing" || status === "submitting";
  const canSubmit = !amountInvalid && amount !== "" && !isBusy && status !== "success";

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current && !isBusy) {
      onClose();
    }
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Deploy ${strategy.name} strategy`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        background: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "440px",
          background: "#0e0e0e",
          border: "1px solid #1c1c1c",
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 20px 16px",
            borderBottom: "1px solid #141414",
          }}
        >
          <div>
            <p
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: "18px",
                fontWeight: 400,
                color: "#f0ece4",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              Deploy {strategy.name}
            </p>
            <p style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>
              {strategy.subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close"
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "6px",
              border: "1px solid #1e1e1e",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isBusy ? "not-allowed" : "pointer",
              opacity: isBusy ? 0.4 : 1,
            }}
          >
            <X size={13} style={{ color: "#666" }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* Allocation breakdown */}
          <div>
            <p
              style={{
                fontSize: "10px",
                fontWeight: 500,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#444",
                marginBottom: "10px",
              }}
            >
              Allocation
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              {strategy.pools.map((pool, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "9px 12px",
                    background: "#0a0a0a",
                    border: "1px solid #191919",
                    borderRadius: "8px",
                  }}
                >
                  {/* Color dot */}
                  <div
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      background: getProtocolColor(pool.protocol),
                      flexShrink: 0,
                    }}
                  />
                  {/* Protocol + pair */}
                  <span style={{ fontSize: "12px", color: "#888", fontWeight: 500, flexShrink: 0 }}>
                    {pool.protocol}
                  </span>
                  <span style={{ fontSize: "11px", color: "#444", flexShrink: 0 }}>
                    {pool.pair}
                  </span>
                  {/* Spacer */}
                  <div style={{ flex: 1 }} />
                  {/* Allocation % */}
                  <span style={{ fontSize: "11px", color: "#555", fontWeight: 500, flexShrink: 0 }}>
                    {pool.allocation}%
                  </span>
                  {/* APR */}
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#3ecf7e",
                      flexShrink: 0,
                      minWidth: "36px",
                      textAlign: "right",
                    }}
                  >
                    {pool.apr}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Estimated APY callout */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              background: `${strategy.accentColor}0a`,
              border: `1px solid ${strategy.accentColor}22`,
              borderRadius: "8px",
            }}
          >
            <span style={{ fontSize: "11px", color: "#555" }}>Estimated APY</span>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: "18px",
                color: strategy.accentColor,
                lineHeight: 1,
                letterSpacing: "-0.01em",
              }}
            >
              {strategy.apy}
            </span>
          </div>

          {/* Amount input */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "7px",
              }}
            >
              <label
                htmlFor={inputId}
                style={{
                  fontSize: "10px",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#444",
                }}
              >
                USDC Amount
              </label>
              {usdcBalance !== null && (
                <button
                  type="button"
                  onClick={() => {
                    if (usdcBalance > 0n) {
                      const formatted = (Number(usdcBalance) / TOKEN_UNIT).toFixed(2);
                      setAmount(formatted);
                    }
                  }}
                  style={{
                    fontSize: "11px",
                    color: "#444",
                    background: "transparent",
                    border: "none",
                    cursor: usdcBalance > 0n ? "pointer" : "default",
                    padding: 0,
                  }}
                >
                  Balance:{" "}
                  <span style={{ color: "#666", fontVariantNumeric: "tabular-nums" }}>
                    {formatBalance(usdcBalance)} USDC
                  </span>
                </button>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <input
                id={inputId}
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); }}
                disabled={isBusy}
                aria-invalid={amountInvalid}
                aria-describedby={amountInvalid ? `${inputId}-error` : undefined}
                style={{
                  width: "100%",
                  padding: "10px 48px 10px 12px",
                  background: "#0a0a0a",
                  border: `1px solid ${amountInvalid ? "#7f2323" : "#1c1c1c"}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Code', monospace",
                  color: "#e8e4dc",
                  outline: "none",
                  boxSizing: "border-box",
                  opacity: isBusy ? 0.5 : 1,
                }}
                onFocus={(e) => {
                  if (!amountInvalid) e.currentTarget.style.borderColor = "#2a2a2a";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = amountInvalid ? "#7f2323" : "#1c1c1c";
                }}
              />
              <span
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#444",
                  pointerEvents: "none",
                }}
              >
                USDC
              </span>
            </div>
            {amountInvalid && (
              <p
                id={`${inputId}-error`}
                role="alert"
                style={{ fontSize: "11px", color: "#e05252", marginTop: "5px" }}
              >
                Enter a valid amount greater than 0
              </p>
            )}
          </div>

          {/* Transaction log — shows passkey hint until deposit starts */}
          {logs.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 0" }}>
              <Fingerprint size={13} style={{ color: "#333", flexShrink: 0 }} />
              <span style={{ fontSize: "11px", color: "#333" }}>
                Your passkey will sign this transaction
              </span>
            </div>
          ) : (
            <TxLogPanel
              logs={logs}
              defaultOpen={status === "error"}
              maxHeight={200}
            />
          )}
        </div>

        {/* Footer actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
            padding: "14px 20px 18px",
            borderTop: "1px solid #141414",
          }}
        >
          {/* Cancel */}
          {status !== "success" && (
            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "9px 18px",
                borderRadius: "8px",
                border: "1px solid #1e1e1e",
                background: "transparent",
                color: isBusy ? "#2a2a2a" : "#555",
                cursor: isBusy ? "not-allowed" : "pointer",
                transition: "border-color 150ms ease, color 150ms ease",
              }}
              onMouseEnter={(e) => {
                if (!isBusy) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#333";
                  (e.currentTarget as HTMLButtonElement).style.color = "#888";
                }
              }}
              onMouseLeave={(e) => {
                if (!isBusy) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#1e1e1e";
                  (e.currentTarget as HTMLButtonElement).style.color = "#555";
                }
              }}
            >
              Cancel
            </button>
          )}

          {/* Deposit / Done */}
          {status === "success" ? (
            <button
              type="button"
              onClick={onClose}
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "9px 22px",
                borderRadius: "8px",
                border: "none",
                background: "#1a1a1a",
                color: "#3ecf7e",
                cursor: "pointer",
              }}
            >
              Done
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { void handleDeposit(); }}
              disabled={!canSubmit}
              aria-label={`Deposit ${amount || "0"} USDC into ${strategy.name} strategy`}
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "9px 22px",
                borderRadius: "8px",
                border: "none",
                background: !canSubmit ? "#141414" : strategy.deployColor,
                color: !canSubmit ? "#2a2a2a" : strategy.deployTextColor,
                cursor: !canSubmit ? "not-allowed" : "pointer",
                transition: "opacity 150ms ease",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
              onMouseEnter={(e) => {
                if (canSubmit) (e.currentTarget as HTMLButtonElement).style.opacity = "0.88";
              }}
              onMouseLeave={(e) => {
                if (canSubmit) (e.currentTarget as HTMLButtonElement).style.opacity = "1";
              }}
            >
              {isBusy ? (
                <>
                  <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} aria-hidden="true" />
                  {status === "signing" ? "Signing..." : "Depositing..."}
                </>
              ) : (
                <>
                  Deposit
                  <span aria-hidden="true" style={{ fontSize: "13px" }}>→</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Withdraw modal
// ---------------------------------------------------------------------------

type WithdrawStatus = "idle" | "signing" | "submitting" | "success" | "error";

interface WithdrawModalProps {
  strategy: Strategy;
  onClose: () => void;
}

function WithdrawModal({ strategy, onClose }: WithdrawModalProps) {
  const { queryBalance, batchRedeem, redeemAqua, claimAqua } = usePasskey();
  const [status, setStatus] = useState<WithdrawStatus>("idle");
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const busyRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const addLog = useCallback(
    (level: LogEntry["level"], message: string, txHash?: string) => {
      setLogs((prev) => [...prev, createLogEntry(level, message, txHash)]);
    },
    [],
  );

  // Trap focus — close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && status !== "signing" && status !== "submitting") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => { document.removeEventListener("keydown", handleKeyDown); };
  }, [onClose, status]);

  // Prevent background scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleWithdraw = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setStatus("signing");

    const deadline = Math.floor(Date.now() / 1000) + 600;
    const isFloat = strategy.slug === "FLOAT";

    try {
      // Step 1: query LP balances
      addLog("info", "Querying LP positions...");
      const balanceQueries: Promise<bigint>[] = [
        queryBalance(POOL_ADDRESSES.AQUA_LP_TOKEN),
        queryBalance(POOL_ADDRESSES.SOROSWAP_PAIR),
      ];
      if (isFloat) {
        balanceQueries.push(queryBalance(POOL_ADDRESSES.PHOENIX_LP_TOKEN));
      }
      const balances = await Promise.all(balanceQueries);
      const aquaLpRaw = balances[0];
      const soroswapLpRaw = balances[1];
      const phoenixLpRaw = isFloat ? balances[2] : 0n;

      const aquaFormatted = (Number(aquaLpRaw) / TOKEN_UNIT).toFixed(4);
      const soroswapFormatted = (Number(soroswapLpRaw) / TOKEN_UNIT).toFixed(4);
      const phoenixFormatted = isFloat ? (Number(phoenixLpRaw) / TOKEN_UNIT).toFixed(4) : "0";
      const positionSummary = isFloat
        ? `Found: ${aquaFormatted} Aqua LP, ${phoenixFormatted} Phoenix LP, ${soroswapFormatted} Soroswap LP`
        : `Found: ${aquaFormatted} Aqua LP, ${soroswapFormatted} Soroswap LP`;
      addLog("info", positionSummary);

      if (aquaLpRaw === 0n && soroswapLpRaw === 0n && phoenixLpRaw === 0n) {
        addLog("error", "No LP positions found to withdraw.");
        setStatus("error");
        busyRef.current = false;
        return;
      }

      setStatus("submitting");

      // Step 2: Soroswap + Phoenix withdrawal via batchRedeem
      // Phoenix has no checkpoint hooks, so batchRedeem works fine for it.
      const redeemPlans: RedeemPlan[] = [];
      if (soroswapLpRaw > 0n) {
        redeemPlans.push({
          lpToken: POOL_ADDRESSES.SOROSWAP_PAIR,
          lpAmount: soroswapLpRaw,
          adapterId: BigInt(ADAPTER_IDS.SOROSWAP),
          poolAddress: POOL_ADDRESSES.SOROSWAP_PAIR,
        });
      }
      if (phoenixLpRaw > 0n) {
        redeemPlans.push({
          lpToken: POOL_ADDRESSES.PHOENIX_LP_TOKEN,
          lpAmount: phoenixLpRaw,
          adapterId: BigInt(ADAPTER_IDS.PHOENIX),
          poolAddress: POOL_ADDRESSES.PHOENIX_POOL,
        });
      }

      if (redeemPlans.length > 0) {
        const protocols = redeemPlans.map((p) =>
          p.adapterId === BigInt(ADAPTER_IDS.SOROSWAP) ? "Soroswap" : "Phoenix",
        );
        addLog("info", `Withdrawing from ${protocols.join(" + ")}...`);
        const result = await batchRedeem(redeemPlans, deadline);
        if (result?.success) {
          addLog("success", `${protocols.join(" + ")} withdrawal complete.`, result.hash);
          void recordActivity({
            txHash: result.hash,
            action: "withdraw",
            strategyId: strategy.slug,
            amounts: {
              soroswapLp: Number(soroswapLpRaw) / TOKEN_UNIT,
              ...(phoenixLpRaw > 0n ? { phoenixLp: Number(phoenixLpRaw) / TOKEN_UNIT } : {}),
            },
            status: "success",
          });
        } else {
          const errMsg = result?.error ?? "Batch withdrawal failed.";
          addLog("error", errMsg.length > 200 ? errMsg.slice(0, 200) + "..." : errMsg);
          setStatus("error");
          void recordActivity({
            txHash: "",
            action: "withdraw",
            strategyId: strategy.slug,
            status: "error",
            errorMessage: errMsg.slice(0, 200),
          });
          busyRef.current = false;
          return;
        }
      }

      // Step 3: Aqua withdrawal via direct pool call
      if (aquaLpRaw > 0n) {
        addLog("info", "Withdrawing from Aqua (direct pool)...");
        const result = await redeemAqua(POOL_ADDRESSES.AQUA_POOL, aquaLpRaw);
        if (result?.success) {
          addLog("success", "Aqua withdrawal complete.", result.hash);
          void recordActivity({
            txHash: result.hash,
            action: "withdraw",
            strategyId: strategy.slug,
            amounts: { aquaLp: Number(aquaLpRaw) / TOKEN_UNIT },
            status: "success",
          });
        } else {
          const errMsg = result?.error ?? "Aqua withdrawal failed.";
          addLog("error", errMsg.length > 200 ? errMsg.slice(0, 200) + "..." : errMsg);
          setStatus("error");
          void recordActivity({
            txHash: "",
            action: "withdraw",
            strategyId: strategy.slug,
            status: "error",
            errorMessage: errMsg.slice(0, 200),
          });
          busyRef.current = false;
          return;
        }
      }

      // Step 4: Auto-claim Aqua rewards (non-fatal)
      if (aquaLpRaw > 0n) {
        try {
          addLog("info", "Claiming Aqua rewards...");
          const claimResult = await claimAqua(POOL_ADDRESSES.AQUA_POOL);
          if (claimResult?.success) {
            addLog("info", "Aqua rewards claimed.", claimResult.hash);
            void recordActivity({
              txHash: claimResult.hash,
              action: "claim",
              strategyId: strategy.slug,
              status: "success",
            });
          }
        } catch {
          // Non-fatal — rewards claim failure does not block withdrawal success
        }
      }

      setStatus("success");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Withdrawal failed. Please try again.";
      addLog("error", msg.length > 200 ? msg.slice(0, 200) + "..." : msg);
      setStatus("error");
      void recordActivity({
        txHash: "",
        action: "withdraw",
        strategyId: strategy.slug,
        status: "error",
        errorMessage: msg.slice(0, 200),
      });
    } finally {
      busyRef.current = false;
    }
  }, [queryBalance, batchRedeem, redeemAqua, claimAqua, addLog, strategy.slug]);

  const isBusy = status === "signing" || status === "submitting";

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current && !isBusy) {
      onClose();
    }
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Withdraw from ${strategy.name} strategy`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        background: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "440px",
          background: "#0e0e0e",
          border: "1px solid #1c1c1c",
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 20px 16px",
            borderBottom: "1px solid #141414",
          }}
        >
          <div>
            <p
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: "18px",
                fontWeight: 400,
                color: "#f0ece4",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              Withdraw from {strategy.name}
            </p>
            <p style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>
              Removes all LP positions and returns tokens to your account
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close"
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "6px",
              border: "1px solid #1e1e1e",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isBusy ? "not-allowed" : "pointer",
              opacity: isBusy ? 0.4 : 1,
            }}
          >
            <X size={13} style={{ color: "#666" }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* Warning notice */}
          <div
            style={{
              padding: "10px 14px",
              background: "#100d05",
              border: "1px solid #2a1f0a",
              borderRadius: "8px",
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: "13px", flexShrink: 0, marginTop: "1px" }}>&#9888;</span>
            <p style={{ fontSize: "11px", color: "#7a6030", lineHeight: 1.6 }}>
              This will withdraw all LP positions from the {strategy.name} strategy
              {strategy.slug === "FLOAT" ? " across Aqua, Phoenix, and Soroswap" : " across Soroswap and Aqua"}.
              Tokens will be returned to your smart account.
            </p>
          </div>

          {/* Pool breakdown */}
          <div>
            <p
              style={{
                fontSize: "10px",
                fontWeight: 500,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#444",
                marginBottom: "10px",
              }}
            >
              Positions to withdraw
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              {strategy.pools.map((pool, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "9px 12px",
                    background: "#0a0a0a",
                    border: "1px solid #191919",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      background: getProtocolColor(pool.protocol),
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: "12px", color: "#888", fontWeight: 500, flexShrink: 0 }}>
                    {pool.protocol}
                  </span>
                  <span style={{ fontSize: "11px", color: "#444", flexShrink: 0 }}>
                    {pool.pair}
                  </span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: "11px", color: "#555", fontWeight: 500, flexShrink: 0 }}>
                    {pool.allocation}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Transaction log — passkey hint until withdrawal starts */}
          {logs.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 0" }}>
              <Fingerprint size={13} style={{ color: "#333", flexShrink: 0 }} />
              <span style={{ fontSize: "11px", color: "#333" }}>
                Your passkey will sign each withdrawal transaction
              </span>
            </div>
          ) : (
            <TxLogPanel
              logs={logs}
              defaultOpen={status === "error" || status === "success"}
              maxHeight={220}
            />
          )}
        </div>

        {/* Footer actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
            padding: "14px 20px 18px",
            borderTop: "1px solid #141414",
          }}
        >
          {/* Cancel / Done */}
          {status !== "success" ? (
            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "9px 18px",
                borderRadius: "8px",
                border: "1px solid #1e1e1e",
                background: "transparent",
                color: isBusy ? "#2a2a2a" : "#555",
                cursor: isBusy ? "not-allowed" : "pointer",
                transition: "border-color 150ms ease, color 150ms ease",
              }}
              onMouseEnter={(e) => {
                if (!isBusy) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#333";
                  (e.currentTarget as HTMLButtonElement).style.color = "#888";
                }
              }}
              onMouseLeave={(e) => {
                if (!isBusy) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#1e1e1e";
                  (e.currentTarget as HTMLButtonElement).style.color = "#555";
                }
              }}
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "9px 22px",
                borderRadius: "8px",
                border: "none",
                background: "#1a1a1a",
                color: "#3ecf7e",
                cursor: "pointer",
              }}
            >
              Done
            </button>
          )}

          {/* Withdraw / Loading */}
          {status !== "success" && (
            <button
              type="button"
              onClick={() => { void handleWithdraw(); }}
              disabled={isBusy}
              aria-label={`Withdraw all positions from ${strategy.name} strategy`}
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "9px 22px",
                borderRadius: "8px",
                border: isBusy ? "none" : "1px solid #3a1a1a",
                background: isBusy ? "#1a1010" : "#170a0a",
                color: isBusy ? "#4a2020" : "#e05252",
                cursor: isBusy ? "not-allowed" : "pointer",
                transition: "border-color 150ms ease, background 150ms ease, color 150ms ease",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
              onMouseEnter={(e) => {
                if (!isBusy) {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.borderColor = "#5a2020";
                  el.style.background = "#200e0e";
                  el.style.color = "#f87171";
                }
              }}
              onMouseLeave={(e) => {
                if (!isBusy) {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.borderColor = "#3a1a1a";
                  el.style.background = "#170a0a";
                  el.style.color = "#e05252";
                }
              }}
            >
              {isBusy ? (
                <>
                  <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} aria-hidden="true" />
                  {status === "signing" ? "Signing..." : "Withdrawing..."}
                </>
              ) : (
                "Withdraw All"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function StrategiesPage() {
  const { isConnected, contractId } = usePasskey();
  const { data: session } = useSession();
  const [activeModal, setActiveModal] = useState<Strategy | null>(null);
  const [withdrawModal, setWithdrawModal] = useState<Strategy | null>(null);

  // Wallet is ready if passkey is connected, or the user's session says they have a wallet
  const hasWallet = isConnected || !!session?.user.hasWallet;

  // Query live LP balances for the connected smart account
  const { balances } = useSmartAccountBalances(contractId ?? undefined);
  const hasFixedPosition = balances.aquaLp > 0 || balances.soroswapLp > 0;

  const openModal = (strategy: Strategy) => {
    if (strategy.status === "coming_soon") return;
    if (!hasWallet) return;
    setActiveModal(strategy);
  };

  const closeModal = () => { setActiveModal(null); };

  const openWithdrawModal = (strategy: Strategy) => {
    setWithdrawModal(strategy);
  };

  const closeWithdrawModal = () => { setWithdrawModal(null); };

  return (
    <PageLayout>
      <div style={{ maxWidth: "720px", margin: "0 auto", width: "100%" }}>

        {/* Page header */}
        <div className="anim-fadeSlideInUp-500ms" style={{ marginBottom: "28px" }}>
          <h1
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: "36px",
              color: "#f0ece4",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              fontWeight: 400,
              marginBottom: "8px",
            }}
          >
            Strategies
          </h1>
          <p style={{ fontSize: "11px", color: "#888", lineHeight: 1.75 }}>
            Choose a yield strategy for your USDC. Each strategy allocates deposits across Stellar DEX protocols automatically.
          </p>
        </div>

        {/* Wallet gate notice */}
        {!hasWallet && (
          <div
            className="anim-fadeSlideInUp-500ms"
            style={{
              marginBottom: "16px",
              padding: "11px 16px",
              background: "#0c0c0c",
              border: "1px solid #1e1e1e",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Fingerprint size={14} style={{ color: "#444", flexShrink: 0 }} />
            <p style={{ fontSize: "11px", color: "#555", lineHeight: 1.5 }}>
              Connect or deploy a smart account to activate strategies.{" "}
              <Link
                href="/tests/demo-passkeys"
                style={{ color: "#3ecf7e", textDecoration: "none" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "none"; }}
              >
                Set up passkey
              </Link>
            </p>
          </div>
        )}

        {/* Strategy cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {STRATEGIES.map((strategy, index) => {
            const isComingSoon = strategy.status === "coming_soon";
            const isDeployDisabled = isComingSoon || !hasWallet;
            const riskStyles = getRiskBadgeStyles(strategy.riskLevel);

            return (
              <div
                key={strategy.id}
                className="anim-fadeSlideInUp-500ms"
                style={{ animationDelay: `${String((index + 1) * 0.1)}s` }}
              >
                <div
                  style={{
                    position: "relative",
                    background: "#0e0e0e",
                    border: "1px solid #1c1c1c",
                    borderRadius: "16px",
                    opacity: isComingSoon ? 0.65 : 1,
                    transition: isComingSoon ? "none" : "border-color 200ms ease",
                  }}
                  onMouseEnter={
                    isComingSoon
                      ? undefined
                      : (e) => {
                          (e.currentTarget as HTMLDivElement).style.borderColor = "#272727";
                        }
                  }
                  onMouseLeave={
                    isComingSoon
                      ? undefined
                      : (e) => {
                          (e.currentTarget as HTMLDivElement).style.borderColor = "#1c1c1c";
                        }
                  }
                >
                  {/* Top bar */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "20px 22px 18px 28px",
                      borderBottom: "1px solid #141414",
                      gap: "16px",
                    }}
                  >
                    {/* Left: icon + name + badges */}
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                      {/* Icon box */}
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "10px",
                          background: "#141414",
                          border: "1px solid #1e1e1e",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <strategy.icon size={18} style={{ color: strategy.accentColor }} />
                      </div>

                      {/* Name */}
                      <span
                        style={{
                          fontFamily: "var(--font-instrument-serif), Georgia, serif",
                          fontStyle: "italic",
                          fontSize: "18px",
                          fontWeight: 400,
                          color: "#f0ece4",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {strategy.name}
                      </span>

                      {/* Risk badge */}
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase" as const,
                          padding: "2px 8px",
                          borderRadius: "4px",
                          border: `1px solid ${riskStyles.borderColor}`,
                          background: riskStyles.background,
                          color: riskStyles.color,
                        }}
                      >
                        {isComingSoon ? "Coming Soon" : `${strategy.riskLevel} Risk`}
                      </span>

                      {/* Active badge — shown when LP positions are detected */}
                      {strategy.slug === "FIXED" && hasFixedPosition && (
                        <span
                          style={{
                            fontSize: "9px",
                            fontWeight: 600,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase" as const,
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px solid rgba(62,207,126,0.25)",
                            background: "rgba(62,207,126,0.1)",
                            color: "#3ecf7e",
                          }}
                        >
                          Active
                        </span>
                      )}

                      {/* Subtitle */}
                      <span style={{ fontSize: "11px", color: "#555" }}>
                        {strategy.subtitle}
                      </span>
                    </div>

                    {/* Right: APY */}
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: "10px", color: "#444", letterSpacing: "0.08em", textTransform: "uppercase" as const, marginBottom: "2px" }}>
                        Est. APY
                      </div>
                      <div
                        style={{
                          fontFamily: "'Instrument Serif', serif",
                          fontStyle: "italic",
                          fontSize: isComingSoon ? "22px" : "30px",
                          color: isComingSoon ? "#3a3a3a" : strategy.accentColor,
                          lineHeight: 1,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {strategy.apy}
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div
                    style={{
                      padding: "18px 22px 18px 28px",
                      borderBottom: "1px solid #141414",
                    }}
                  >
                    {/* Description */}
                    <p style={{ fontSize: "11px", color: "#5a5a5a", lineHeight: 1.7, marginBottom: "14px" }}>
                      {strategy.description}
                    </p>

                    {/* Token tags */}
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" as const, marginBottom: "14px" }}>
                      {strategy.tokens.map((token) => (
                        <span
                          key={token}
                          style={{
                            fontSize: "10px",
                            fontWeight: 500,
                            color: "#666",
                            background: "#111",
                            border: "1px solid #1e1e1e",
                            borderRadius: "5px",
                            padding: "2px 9px",
                          }}
                        >
                          {token}
                        </span>
                      ))}
                    </div>

                    {/* Allocation bar */}
                    <div style={{ marginBottom: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          height: "3px",
                          borderRadius: "2px",
                          overflow: "hidden",
                          gap: "2px",
                          marginBottom: "10px",
                        }}
                      >
                        {strategy.pools.map((pool, i) => (
                          <div
                            key={i}
                            style={{
                              height: "100%",
                              width: `${String(pool.allocation)}%`,
                              background: getProtocolColor(pool.protocol),
                              borderRadius: "2px",
                              opacity: isComingSoon ? 0.4 : 1,
                            }}
                          />
                        ))}
                      </div>

                      {/* Allocation details */}
                      <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "6px 20px" }}>
                        {strategy.pools.map((pool, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                background: getProtocolColor(pool.protocol),
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ fontSize: "11px", color: "#4a4a4a", fontWeight: 500 }}>
                              {pool.protocol}
                            </span>
                            <span style={{ fontSize: "11px", color: "#383838" }}>
                              {pool.pair}
                            </span>
                            <span style={{ fontSize: "11px", color: "#333" }}>
                              {String(pool.allocation)}%
                            </span>
                            <span style={{ fontSize: "11px", color: "#4a7c5a", fontWeight: 500 }}>
                              {pool.apr}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Active LP position details */}
                    {strategy.slug === "FIXED" && hasFixedPosition && (
                      <div
                        style={{
                          marginTop: "14px",
                          padding: "12px 14px",
                          background: "rgba(62,207,126,0.04)",
                          border: "1px solid rgba(62,207,126,0.1)",
                          borderRadius: "10px",
                          display: "flex",
                          flexDirection: "column" as const,
                          gap: "8px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "10px",
                            fontWeight: 500,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase" as const,
                            color: "rgba(62,207,126,0.5)",
                            marginBottom: "2px",
                          }}
                        >
                          Your positions
                        </p>
                        <div style={{ display: "flex", flexDirection: "column" as const, gap: "5px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <div
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  background: getProtocolColor("aqua"),
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ fontSize: "11px", color: "#4a4a4a", fontWeight: 500 }}>Aqua LP</span>
                            </div>
                            <span
                              style={{
                                fontSize: "11px",
                                color: "#5a5a5a",
                                fontFamily: "ui-monospace, 'SF Mono', monospace",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {balances.aquaLp.toFixed(4)} tokens
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <div
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  background: getProtocolColor("soroswap"),
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ fontSize: "11px", color: "#4a4a4a", fontWeight: 500 }}>Soroswap LP</span>
                            </div>
                            <span
                              style={{
                                fontSize: "11px",
                                color: "#5a5a5a",
                                fontFamily: "ui-monospace, 'SF Mono', monospace",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {balances.soroswapLp.toFixed(4)} tokens
                            </span>
                          </div>
                        </div>
                        <Link
                          href="/portfolio"
                          style={{
                            fontSize: "11px",
                            color: "rgba(62,207,126,0.6)",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            marginTop: "2px",
                            width: "fit-content",
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "#3ecf7e";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "rgba(62,207,126,0.6)";
                          }}
                        >
                          View in Dashboard
                          <ExternalLink size={10} aria-hidden="true" />
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "14px 22px 14px 28px",
                      gap: "12px",
                    }}
                  >
                    {/* Details ghost button */}
                    <button
                      disabled={isComingSoon}
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase" as const,
                        padding: "9px 22px",
                        borderRadius: "8px",
                        border: "1px solid #222",
                        background: "transparent",
                        color: isComingSoon ? "#2a2a2a" : "#555",
                        cursor: isComingSoon ? "not-allowed" : "pointer",
                        transition: "border-color 150ms ease, color 150ms ease",
                      }}
                      onMouseEnter={
                        isComingSoon
                          ? undefined
                          : (e) => {
                              const el = e.currentTarget;
                              el.style.borderColor = "#333";
                              el.style.color = "#888";
                            }
                      }
                      onMouseLeave={
                        isComingSoon
                          ? undefined
                          : (e) => {
                              const el = e.currentTarget;
                              el.style.borderColor = "#222";
                              el.style.color = "#555";
                            }
                      }
                    >
                      Details
                    </button>

                    {/* Withdraw button — only shown for FIXED when active */}
                    {strategy.slug === "FIXED" && hasFixedPosition && (
                      <button
                        onClick={() => { openWithdrawModal(strategy); }}
                        aria-label={`Withdraw from ${strategy.name} strategy`}
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase" as const,
                          padding: "9px 22px",
                          borderRadius: "8px",
                          border: "1px solid #222",
                          background: "#141414",
                          color: "#888",
                          cursor: "pointer",
                          transition: "border-color 150ms ease, color 150ms ease",
                        }}
                        onMouseEnter={(e) => {
                          const el = e.currentTarget;
                          el.style.borderColor = "#333";
                          el.style.color = "#d4cfc6";
                        }}
                        onMouseLeave={(e) => {
                          const el = e.currentTarget;
                          el.style.borderColor = "#222";
                          el.style.color = "#888";
                        }}
                      >
                        Withdraw
                      </button>
                    )}

                    {/* Deploy button — wallet-gated */}
                    <div style={{ position: "relative" }}>
                      <button
                        disabled={isDeployDisabled}
                        onClick={() => { openModal(strategy); }}
                        aria-label={
                          isComingSoon
                            ? `${strategy.name} coming soon`
                            : !hasWallet
                            ? `Connect wallet to deploy ${strategy.name}`
                            : `Deploy ${strategy.name} strategy`
                        }
                        aria-disabled={isDeployDisabled}
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase" as const,
                          padding: "9px 22px",
                          borderRadius: "8px",
                          border: isDeployDisabled ? "1px solid #1e1e1e" : "none",
                          background: isDeployDisabled ? "#111" : strategy.deployColor,
                          color: isDeployDisabled ? "#2a2a2a" : strategy.deployTextColor,
                          cursor: isDeployDisabled ? "not-allowed" : "pointer",
                          transition: "opacity 150ms ease",
                        }}
                        onMouseEnter={
                          isDeployDisabled
                            ? undefined
                            : (e) => {
                                (e.currentTarget as HTMLButtonElement).style.opacity = "0.85";
                              }
                        }
                        onMouseLeave={
                          isDeployDisabled
                            ? undefined
                            : (e) => {
                                (e.currentTarget as HTMLButtonElement).style.opacity = "1";
                              }
                        }
                      >
                        {isComingSoon
                          ? "Coming Soon"
                          : !hasWallet
                          ? "Connect Wallet"
                          : "Deploy Strategy"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div
          className="anim-fadeSlideInUp-500ms"
          style={{ animationDelay: "0.5s", marginTop: "10px" }}
        >
          <div
            style={{
              border: "1px dashed #1e1e1e",
              borderRadius: "16px",
              padding: "16px 22px",
            }}
          >
            <p style={{ fontSize: "11px", color: "#444", lineHeight: 1.75 }}>
              Strategies are frontend-defined allocation templates. When you deploy a strategy, your smart account
              splits USDC deposits across the selected pools via the Hoops Router contract. All funds remain
              non-custodial in your on-chain smart account.{" "}
              <Link
                href="/tests/demo-passkeys"
                style={{ color: "#3ecf7e", textDecoration: "none" }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.textDecoration = "none";
                }}
              >
                Try the testnet demo
              </Link>
            </p>
          </div>
        </div>

      </div>

      {/* Deposit modal — rendered via portal-like pattern at page root */}
      {activeModal !== null && (
        <DepositModal strategy={activeModal} onClose={closeModal} />
      )}

      {/* Withdraw modal */}
      {withdrawModal !== null && (
        <WithdrawModal strategy={withdrawModal} onClose={closeWithdrawModal} />
      )}
    </PageLayout>
  );
}
